import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Grid, Tab, Tabs, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { omit } from 'lodash';
import { usePostHog } from 'posthog-js/react';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService, Language, OrderPageData, PartialLabelData } from '../../../api';
import { InformationSectionLanguageTab } from './InformationSectionLanguageTab';
import { OrderPageUpdate } from '../../OrderPageSettingsPage';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import { dynamicLabelSchema } from './dynamicLabelSchema';
import { FormRichTextField } from '../../formComponents/FormRichTextField';
import { SettingsRow } from '../../formComponents/SettingsRow';
import { PartialLabelListDrawer } from './PartialLabelListDrawer';
import { SectionSaveRegistrar } from '../../saveHeader/SectionSaveRegistrar';
import type { ChangeEntry } from '../../saveHeader/SaveHeaderContext';
import { htmlValuesDiffer, renderRichDiffPair, renderTextDiffPair } from '../../saveHeader/richTextDiff';
import { collect, diffIdList, diffPartialLabelList } from '../../saveHeader/diffBuilders';
import { SettingsPageTabs } from '../../SettingsPageTabs';
import { OrderPagesMultiSelect } from '../../../userSettings/OrderPagesMultiSelect';
import { getOrderPages } from '../../../utils/AccountUtils';
import { useLanguage } from '../../../common/GeneralUtils';

type InformationSectionProps = {
  orderPage: OrderPageData;
  languages: Language[];
  orderPageInitialValues: OrderPageUpdate;
  updateLocalAccount: KeyedMutator<AccountData>;
  account: AccountData;
};

export type InformationFormValues = {
  label: { [key in Language]?: string };
  description: { [key in Language]?: string } | undefined;
  activity_label?: { [key in Language]?: string };
  order_page_items_descriptions?: PartialLabelData[];
  practical_info_descriptions?: PartialLabelData[];
  related_order_page_ids: string[];
};

export const InformationSection = ({
  orderPage,
  languages,
  orderPageInitialValues,
  updateLocalAccount,
  account,
}: InformationSectionProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const [activeTab, setActiveTab] = useState(languages[0]);
  const { readOnly } = UseAuthPermissions();
  const posthog = usePostHog();

  const handleTabChange = (event: React.SyntheticEvent, newValue: Language) => {
    setActiveTab(newValue);
  };

  const validationSchema = yup.object().shape({
    label: yup.object().shape(dynamicLabelSchema(t, languages, true)),
    description: yup.object().shape(dynamicLabelSchema(t, languages)).nullable(),
    activity_label: yup.object().shape(dynamicLabelSchema(t, languages)),
    related_order_page_ids: yup.array().of(yup.string()),
  });

  const initialValues: InformationFormValues = {
    label: orderPage.label,
    description: orderPage.description,
    activity_label: orderPage.activity_label,
    order_page_items_descriptions: orderPage.order_page_items_descriptions || [],
    practical_info_descriptions: orderPage.practical_info_descriptions || [],
    related_order_page_ids: orderPage.related_order_page_ids || [],
  };
  const handleSubmit = async (values: InformationFormValues, { setStatus }: FormikHelpers<InformationFormValues>) => {
    try {
      posthog.capture('Editing settings', { section: 'informationSection', settings: values });
      const updatedAccount = await AccountService.updateOrderPage(
        omit(
          {
            ...orderPageInitialValues,
            duration: (orderPageInitialValues.duration || 0) * 60,
            ...values,
            id: orderPage.id,
          },
          'availability_settings.restrictions',
          'objects',
        ),
      );
      await updateLocalAccount(updatedAccount);
    } catch (e) {
      setStatus({ error: (e as Error)?.message || t('saveHeader.errorGeneric') });
      throw e;
    }
  };

  const orderPageLabel = (id: string) => {
    const relatedOrderPage = getOrderPages(account, true).find((candidate) => candidate.id === id);
    return relatedOrderPage?.label?.[language] || id;
  };

  const getChanges = (values: InformationFormValues, iv: InformationFormValues): ChangeEntry[] => {
    const changes: ChangeEntry[] = [];
    languages.forEach((lang) => {
      if (values.label?.[lang] !== iv.label?.[lang]) {
        changes.push({
          label: `${t('saveHeader.name')} (${t(`emailTemplates.${lang}`)})`,
          ...renderTextDiffPair(iv.label?.[lang], values.label?.[lang]),
        });
      }
      if (htmlValuesDiffer(values.description?.[lang], iv.description?.[lang])) {
        changes.push({
          label: `${t('editOrderPage.orderPageDescription')} (${t(`emailTemplates.${lang}`)})`,
          ...renderRichDiffPair(iv.description?.[lang], values.description?.[lang]),
        });
      }
      if (values.activity_label?.[lang] !== iv.activity_label?.[lang]) {
        changes.push({
          label: `${t('editOrderPage.activityLabel', { defaultValue: 'Activity label' })} (${t(
            `emailTemplates.${lang}`,
          )})`,
          ...renderTextDiffPair(iv.activity_label?.[lang], values.activity_label?.[lang]),
        });
      }
    });
    return [
      ...changes,
      ...collect(
        diffPartialLabelList(
          t('editOrderPage.whatsIncluded'),
          iv.order_page_items_descriptions,
          values.order_page_items_descriptions,
          languages,
          t,
        ),
        diffPartialLabelList(
          t('editOrderPage.practicalInfo'),
          iv.practical_info_descriptions,
          values.practical_info_descriptions,
          languages,
          t,
        ),
        diffIdList(
          t('editOrderPage.relatedOrderPages'),
          iv.related_order_page_ids,
          values.related_order_page_ids,
          orderPageLabel,
        ),
      ),
    ];
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {(formik) => {
        const { values, isSubmitting, errors, touched, handleChange } = formik;
        const formIsDisabled = readOnly || isSubmitting;
        return (
          <Form onSubmit={formik.handleSubmit}>
            <SectionSaveRegistrar tabKey={SettingsPageTabs.INFORMATION} getChanges={getChanges} disabled={readOnly} />
            {languages.length > 1 && (
              <Grid container justifyContent="flex-start" sx={{ pt: 2 }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="language tabs">
                  {languages.map((lang) => (
                    <Tab key={lang} label={t(`emailTemplates.${lang}`)} value={lang} />
                  ))}
                </Tabs>
              </Grid>
            )}
            <Grid container>
              {languages.map((lang) => (
                <Grid key={lang} hidden={activeTab !== lang} sx={{ width: '100%' }}>
                  {activeTab === lang && <InformationSectionLanguageTab language={lang} disabled={formIsDisabled} />}
                </Grid>
              ))}
            </Grid>

            <Grid container flexDirection="column">
              <SettingsRow label={t('editOrderPage.orderPageDescription')}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('editOrderPage.minisiteDetailsSubtitle')}
                </Typography>
                <FormRichTextField labelFieldName="description" disabled={formIsDisabled} />
              </SettingsRow>

              <Field name="order_page_items_descriptions">
                {({ field, form }: FieldProps<PartialLabelData[]>) => (
                  <PartialLabelListDrawer
                    items={field.value || []}
                    onChange={(items) => form.setFieldValue(field.name, items)}
                    title={t('editOrderPage.whatsIncluded')}
                    subtitle={t('editOrderPage.whatsIncludedSubtitle')}
                    addButtonText={t('editOrderPage.addWhatsIncludedItem')}
                    emptyText={t('editOrderPage.noWhatsIncludedItems')}
                    dialogTitle={t('editOrderPage.whatsIncludedItem')}
                    fieldLabel={t('editOrderPage.itemText')}
                    disabled={formIsDisabled}
                  />
                )}
              </Field>

              <Field name="practical_info_descriptions">
                {({ field, form }: FieldProps<PartialLabelData[]>) => (
                  <PartialLabelListDrawer
                    items={field.value || []}
                    onChange={(items) => form.setFieldValue(field.name, items)}
                    title={t('editOrderPage.practicalInfo')}
                    subtitle={t('editOrderPage.practicalInfoSubtitle')}
                    addButtonText={t('editOrderPage.addPracticalInfoItem')}
                    emptyText={t('editOrderPage.noPracticalInfoItems')}
                    dialogTitle={t('editOrderPage.practicalInfoItem')}
                    fieldLabel={t('editOrderPage.itemText')}
                    disabled={formIsDisabled}
                  />
                )}
              </Field>

              <SettingsRow label={t('editOrderPage.relatedOrderPages')} isLast>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('editOrderPage.relatedOrderPagesCaption')}
                </Typography>
                <OrderPagesMultiSelect
                  exceptOrderPageId={orderPage.id}
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  orderPagesFieldName="related_order_page_ids"
                  account={account}
                />
              </SettingsRow>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
